import type { Message, MessageBodyType } from '@/types/messages.ts';
import { NO_DATE } from './dateUtils.ts';

/**
 * Helper function to check if message has template buttons
 */
export const hasButtons = (message: Message): boolean => {
  if (message.type === 'incoming_message') return false;

  const msgWithBody = message as Message & { body?: MessageBodyType };

  if (!msgWithBody.body) return false;

  if (msgWithBody.body.type === 'template') {
    return (
      !!msgWithBody.body.template.buttons && msgWithBody.body.template.buttons.length > 0
    );
  }

  return msgWithBody.body.type === 'button';
};

/**
 * Helper function to get template buttons
 */
export const getButtons = (
  message: Message,
): { id?: string; title: string; description?: string }[] => {
  const msgWithBody = message as Message & { body?: MessageBodyType };

  if (!msgWithBody.body) return [];

  if (msgWithBody.body.type === 'template') {
    return (
      msgWithBody.body.template.buttons?.map((button) => {
        if (typeof button === 'string') {
          return { title: button };
        }
        return { id: button.id, title: button.title, description: button.description };
      }) || []
    );
  }

  return msgWithBody.body.type === 'button'
    ? [{ id: msgWithBody.body.id, title: msgWithBody.body.text }]
    : [];
};

/**
 * Format date and time for message display
 * Converts UTC date strings to local timezone
 */
export const formatMessageDateTime = (dateString: string | null): string => {
  if (!dateString) return NO_DATE;

  // Ensure the date string is treated as UTC if it doesn't have a timezone indicator
  const utcDateString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
  const date = new Date(utcDateString);
  const now = new Date();

  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }

  return date.toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
    day: 'numeric',
  });
};

/** The day a message was written, without the time: "Oct 17". */
export const formatMessageDate = (dateString: string): string => {
  const utcDateString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
  return new Date(utcDateString).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

/** When somebody last wrote, as a list reads it: the time for today, the day for anything older. */
export const formatMessageDay = (dateString: string): string => {
  const utcDateString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
  const date = new Date(utcDateString);
  if (date.toDateString() === new Date().toDateString()) {
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
  return formatMessageDate(dateString);
};

export { formatFileSize } from '@wundo/shared/fileSize.ts';

/**
 * Render additional properties (meta data) as JSON string
 */
export const renderAdditionalProperties = (message: Message): string | null => {
  if (!message.meta) return null;
  const meta = structuredClone(message.meta);
  delete meta.original_language;
  delete meta.original_body;
  delete meta.trace_id;
  // Shown via the dedicated memory icon, not the debug JSON dump.
  delete meta.offered_memory_handles;
  // Shown as the anchor line under the message, not the debug JSON dump.
  delete meta.anchor;
  // Bookkeeping for which user messages a reply has read; noise when reading a conversation.
  delete meta.answers_up_to;

  if (Object.keys(meta).length === 0) return null;

  return JSON.stringify(meta, null, 2);
};
